import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Row,
  Col,
  Container,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import { Calendar, Users, Clock, Search } from 'lucide-react';
import './Dashboard.module.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Gheader } from './Gheader';
import { Sidebar } from './Sidebar';
import styles from './Deal.module.css';
import apiService from '../api/apiService';
import InvoiceModal from './InvoiceModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditBookingModal from './EditBookingModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import axios from 'axios';
import { getInvoiceByBookingId } from '../utils/getInvoiceByBookingId';

const Booking = () => {
  const hotelId = localStorage.getItem('hotel');
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [guests, setGuests] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    id: null,
    roomId: null,
  });
  const [allInvoices, setAllInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState();
  const [dataChanged, setDataChanged] = useState(0);

  const rowsPerPage = 10;

  // Create a memoized fetchData function that we can reuse
  const fetchData = useCallback(async () => {
    try {
      // Fetch all data in parallel for better performance
      const [bookingsResponse, guestsResponse, invoicesResponse] =
        await Promise.all([
          apiService.get(`/bookings/?hotel=${hotelId}`),
          apiService.get(`/guests/?hotel=${hotelId}`),
          apiService.get(`/invoices/?hotel=${hotelId}`),
        ]);

      setBookings(bookingsResponse);
      setGuests(guestsResponse);
      setAllInvoices(invoicesResponse);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load some data. Please try again.');
    }
  }, [hotelId]);

  // Initial data loading
  useEffect(() => {
    fetchData();
  }, [fetchData, dataChanged]);

  const refreshData = () => {
    // Increment dataChanged to trigger a re-fetch
    setDataChanged(prev => prev + 1);
  };

  const deleteBooking = async () => {
    try {
      const roomId = deleteConfirm.roomId;
      const response = await axios.delete(
        `https://erp.sondermallpalace.com/api/bookings/${deleteConfirm.id}/?hotel=${hotelId}`
      );

      if (response.status === 204) {
        try {
          const updatedRoomStatus = {
            status: 'available',
          };
          await apiService.patch(
            `/rooms/${roomId}/?hotel=${hotelId}`,
            updatedRoomStatus
          );
          toast.success('Room status updated to Available!');
        } catch (error) {
          console.error('Failed to update room status:', error);
        }

        toast.success('Booking deleted successfully!');
        // Update local state immediately for instant UI response
        setBookings(prevBookings =>
          prevBookings.filter(booking => booking.id !== deleteConfirm.id)
        );

        // Refresh data to ensure everything is in sync
        refreshData();
      }
    } catch (error) {
      toast.error('Failed to delete! Please try again.');
      console.log(error);
    } finally {
      setDeleteConfirm({ isOpen: false, id: null, roomId: null });
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSource = activeTab === 'all' || booking.source === activeTab;
    const guestName = booking.guest_info
      ? booking.guest_info.toString().toLowerCase()
      : '';
    const bookingId = booking.booking_id
      ? booking.booking_id.toString().toLowerCase()
      : '';
    const matchesSearch =
      guestName.includes(searchTerm.toLowerCase()) ||
      bookingId.includes(searchTerm.toLowerCase());

    return matchesSource && matchesSearch;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBookings.length / rowsPerPage)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredBookings.slice(indexOfFirstRow, indexOfLastRow);

  const getStats = () => {
    return {
      totalBookings: bookings.length,
      checkedIn: bookings.filter(b => b.status === 'check_in').length,
      upcoming: bookings.filter(b => b.status === 'confirmed').length,
      revenue:
        allInvoices && allInvoices.length > 0
          ? allInvoices.reduce(
              (sum, invoice) => sum + (parseFloat(invoice.total_amount) || 0),
              0
            )
          : bookings.reduce(
              (sum, booking) => sum + (parseFloat(booking.amount) || 0),
              0
            ),
    };
  };

  const stats = getStats();

  const getStatusColor = status => {
    const colors = {
      confirmed: 'bg-success text-white',
      pending: 'bg-warning text-white',
      cancelled: 'bg-danger text-white',
      check_in: 'bg-primary text-white',
      check_out: 'bg-secondary text-white',
    };
    return colors[status] || 'bg-light text-dark';
  };

  const getGuestName = currentGuestId => {
    const matchingGuest = guests.find(guest => guest.id === currentGuestId);
    return matchingGuest ? matchingGuest.name : 'Guest not found';
  };

  const confirmDelete = (id, roomId) => {
    if (!id) return;
    setDeleteConfirm({ isOpen: true, id, roomId });
  };

  const formatStatus = status => {
    const statusMap = {
      check_in: 'Checked-in',
      check_out: 'Checked-out',
    };

    return (
      statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  // Helper function to safely format currency amounts
  const formatCurrency = amount => {
    // Check if the amount is valid and can be converted to a number
    if (amount === null || amount === undefined || isNaN(Number(amount))) {
      return '₹0.00'; // Return a default value for invalid amounts
    }

    // Format the valid amount
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Helper function to safely get booking amount
  const getBookingAmount = booking => {
    // First try to get amount from invoice
    const invoice = getInvoiceByBookingId(booking.id, allInvoices);
    const invoiceAmount = invoice?.total_amount;

    // If invoice amount exists and is valid, use it
    if (
      invoiceAmount !== null &&
      invoiceAmount !== undefined &&
      !isNaN(Number(invoiceAmount))
    ) {
      return invoiceAmount;
    }

    return booking.amount || 0;
  };

  function filterInvoiceFile(allInvoices, bookingId) {
    const matchingInvoice = allInvoices.find(
      invoice => invoice.booking === bookingId
    );

    // If a matching invoice is found, return the invoice_file value
    if (matchingInvoice) {
      return matchingInvoice.ivoice_file;
    }

    // If no matching invoice is found, return null or handle as needed
    return null;
  }

  return (
    <div className={styles.gheader}>
      <div className={styles.dashboardContainer}>
        <Sidebar />
        <div className={styles.mainContent}>
          <div className="min-vh-100 bg-light">
            <Gheader />

            {/* Stats Grid */}
            <Row className="g-4 mb-5 mt-1 px-3">
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Total Bookings</p>
                      <h3 className="fs-2 fw-bold">{stats.totalBookings}</h3>
                    </div>
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Checked In</p>
                      <h3 className="fs-2 fw-bold">{stats.checkedIn}</h3>
                    </div>
                    <Users className="w-6 h-6 text-success" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Upcoming</p>
                      <h3 className="fs-2 fw-bold">{stats.upcoming}</h3>
                    </div>
                    <Clock className="w-6 h-6 text-warning" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Total Revenue</p>
                      <h3 className="fs-2 fw-bold">
                        {formatCurrency(stats.revenue)}
                      </h3>
                    </div>
                    <div className="text-success">12%</div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Filters and Search */}
            <Container fluid>
              <Row className="justify-content-between align-items-center mb-3">
                <Col
                  xs={12}
                  md={6}
                  className="d-flex gap-3 flex-wrap mb-3 mb-md-0"
                >
                  <Button
                    variant={
                      activeTab === 'all' ? 'primary' : 'outline-primary'
                    }
                    onClick={() => setActiveTab('all')}
                  >
                    All Bookings
                  </Button>
                  <Button
                    variant={
                      activeTab === 'agent' ? 'primary' : 'outline-primary'
                    }
                    onClick={() => setActiveTab('agent')}
                  >
                    Agents
                  </Button>
                  <Button
                    variant={
                      activeTab === 'walk_in' ? 'primary' : 'outline-primary'
                    }
                    onClick={() => setActiveTab('walk_in')}
                  >
                    Walk-in
                  </Button>
                </Col>
                <Col
                  xs={12}
                  md={6}
                  className="d-flex justify-content-md-end justify-content-start"
                >
                  <div className="d-none d-flex gap-2">
                    <InputGroup className={styles.searchWidth}>
                      <FormControl
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      <Button variant="outline-primary">
                        <Search className="w-5 h-5" />
                      </Button>
                    </InputGroup>
                    <Button
                      variant="outline-secondary"
                      onClick={refreshData}
                      title="Refresh data"
                    >
                      ↻
                    </Button>
                  </div>
                </Col>
              </Row>
            </Container>

            {/* Bookings Table */}
            <div className="deal-table-container">
              <div className="table-responsive" style={{ minHeight: '350px' }}>
                <table className="deal-table">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Guest Details</th>
                      <th>Check In/Out</th>
                      <th>Room Type</th>
                      <th>Room No.</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map(booking => (
                      <tr key={booking.booking_id || booking.id}>
                        <td>{booking.id}</td>
                        <td>{getGuestName(booking.guest)}</td>
                        <td>
                          {booking.check_in} - {booking.check_out}
                        </td>

                        <td>
                          {booking.room && Array.isArray(booking.room)
                            ? booking.room.map(r => r.room_type_name).join(', ')
                            : booking.room
                            ? booking.room.room_type_name
                            : ''}
                        </td>
                        <td>
                          {booking.room && Array.isArray(booking.room)
                            ? booking.room.map(r => r.room_number).join(', ')
                            : booking.room
                            ? booking.room.room_number
                            : ''}
                        </td>
                        <td>
                          {booking.source === 'walk_in'
                            ? 'Walk-in'
                            : booking.source.charAt(0).toUpperCase() +
                              booking.source.slice(1)}
                        </td>

                        <td>
                          <span
                            className={`badge ${getStatusColor(
                              booking.status
                            )}`}
                          >
                            {formatStatus(booking.status)}
                          </span>
                        </td>

                        <td>{formatCurrency(getBookingAmount(booking))}</td>

                        <td>
                          <div className="options-menu">
                            <button className="options-button">⋮</button>
                            <div className="dropdown-menu">
                              <EditBookingModal
                                editingItem={booking}
                                setBookings={setBookings}
                                bookings={bookings}
                                hotelId={hotelId}
                                onSuccess={refreshData}
                              />
                              <button
                                className="delete-btn"
                                onClick={() =>
                                  confirmDelete(
                                    booking.id,
                                    booking.room && Array.isArray(booking.room)
                                      ? booking.room[0]?.id
                                      : booking.room?.id
                                  )
                                }
                              >
                                Delete
                              </button>

                              {filterInvoiceFile(allInvoices, booking.id) ? (
                                <a
                                  href={filterInvoiceFile(
                                    allInvoices,
                                    booking.id
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <button>Show Invoice</button>
                                </a>
                              ) : (
                                <InvoiceModal
                                  currentBooking={booking}
                                  hotelId={hotelId}
                                  onInvoiceUpdate={refreshData}
                                />
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pagination mt-3">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-outline-primary"
                >
                  Previous
                </button>
                <span className="mx-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-outline-primary"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={deleteConfirm.isOpen}
        onConfirm={deleteBooking}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
        message="Are you sure you want to delete this booking?"
      />

      <ToastContainer autoClose={1500} position="top-center" />
    </div>
  );
};

export default Booking;
